/**
 * LayeredAvatarView — the pure, presentational layered-avatar stack (idle only).
 * No interaction, no animation — safe to render anywhere a user avatar appears
 * (rows, leaderboards, cards). The interactive customizer preview (CharacterStage)
 * wraps this with a poke animation. Memoized: tiny equipped maps, stringify equality.
 */

import { memo } from 'react';
import { View } from 'react-native';
import { Image } from 'expo-image';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { collectEquippedLayers } from './characterCustomizer.helper';
import { layeredAvatarViewStyles as styles, getLayeredAvatarBoxStyle } from './LayeredAvatarView.styles';
import type { EquippedMap } from './characterCustomizer.types';

interface LayeredAvatarViewProps {
  equipped: EquippedMap;
  size: number;
  /** Circular crop (plain crop of the full figure — matches ready avatars in small slots). */
  rounded?: boolean;
}

function LayeredAvatarViewComponent({ equipped, size, rounded = true }: LayeredAvatarViewProps) {
  // Unknown ids (e.g. an item later removed from the catalog) resolve to no layer and are
  // silently skipped here — never a crash.
  const layers = collectEquippedLayers(equipped);
  return (
    <View style={getLayeredAvatarBoxStyle(size, rounded)}>
      {layers.map((layer) => (
        <Image
          key={layer.cat}
          source={{ uri: layer.url }}
          style={styles.layer}
          contentFit="contain"
          cachePolicy="memory-disk"
          transition={120}
          alt={WEB_IMAGE_ALT_DECORATIVE}
        />
      ))}
    </View>
  );
}

function propsEqual(a: LayeredAvatarViewProps, b: LayeredAvatarViewProps): boolean {
  return (
    a.size === b.size &&
    a.rounded === b.rounded &&
    JSON.stringify(a.equipped) === JSON.stringify(b.equipped)
  );
}

export const LayeredAvatarView = memo(LayeredAvatarViewComponent, propsEqual);
